import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Users, UserPlus, UserCheck, MessageCircle, Search, X, Stethoscope, ShieldOff, ShieldCheck } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import UserActionButtons from '../../components/community/UserActionButtons';
import { communityService, ConnectionUser, BlockedUser } from '../../services/communityService';
import toast from 'react-hot-toast';

type TabKey = 'friends' | 'following' | 'followers' | 'blocked';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000';

/**
 * "Prietenii mei" page - central hub for user connections.
 *
 * Tabs:
 *   - Prieteni (mutual follow) – full friends list, primary CTA to message
 *   - Îi urmăresc (users I follow)
 *   - Mă urmăresc (users who follow me)
 *   - Blocați (users blocked by me)
 */
export default function FriendsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = (searchParams.get('tab') as TabKey) || 'friends';

  const [activeTab, setActiveTab] = useState<TabKey>(initialTab);
  const [friends, setFriends] = useState<ConnectionUser[]>([]);
  const [following, setFollowing] = useState<ConnectionUser[]>([]);
  const [followers, setFollowers] = useState<ConnectionUser[]>([]);
  const [blocked, setBlocked] = useState<BlockedUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');

  useEffect(() => {
    loadAll(true);
  }, []);

  const loadAll = async (showLoader = false) => {
    try {
      if (showLoader) setLoading(true);
      const [f, fl, fr, bl] = await Promise.all([
        communityService.getMyFriends().catch(() => []),
        communityService.getMyFollowing().catch(() => []),
        communityService.getMyFollowers().catch(() => []),
        communityService.getBlockedUsers().catch(() => []),
      ]);
      setFriends(f);
      setFollowing(fl);
      setFollowers(fr);
      setBlocked(bl);
    } finally {
      if (showLoader) setLoading(false);
    }
  };

  const changeTab = (tab: TabKey) => {
    setActiveTab(tab);
    setQuery('');
    const params = new URLSearchParams(searchParams);
    params.set('tab', tab);
    setSearchParams(params, { replace: true });
  };

  const handleUnblock = async (userId: string) => {
    try {
      await communityService.unblockUser(userId);
      setBlocked(prev => prev.filter(u => u.id !== userId));
      toast.success('Utilizator deblocat');
    } catch {
      toast.error('Eroare la deblocare');
    }
  };

  const currentList = useMemo(() => {
    if (activeTab === 'blocked') return []; // handled separately
    const list =
      activeTab === 'friends' ? friends : activeTab === 'following' ? following : followers;
    const q = query.trim().toLowerCase();
    if (!q) return list;
    return list.filter((u) => {
      const name = `${u.firstName || ''} ${u.lastName || ''}`.toLowerCase();
      return name.includes(q);
    });
  }, [activeTab, friends, following, followers, query]);

  const filteredBlocked = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return blocked;
    return blocked.filter(u => {
      const name = `${u.firstName || ''} ${u.lastName || ''}`.toLowerCase();
      return name.includes(q);
    });
  }, [blocked, query]);

  const tabs: Array<{ key: TabKey; label: string; count: number; icon: React.ReactNode }> = [
    { key: 'friends', label: 'Prieteni', count: friends.length, icon: <Users className="w-4 h-4" /> },
    { key: 'following', label: 'Îi urmăresc', count: following.length, icon: <UserCheck className="w-4 h-4" /> },
    { key: 'followers', label: 'Mă urmăresc', count: followers.length, icon: <UserPlus className="w-4 h-4" /> },
    { key: 'blocked', label: 'Blocați', count: blocked.length, icon: <ShieldOff className="w-4 h-4" /> },
  ];

  const getPhotoUrl = (photoUrl?: string | null) => {
    if (!photoUrl) return null;
    if (photoUrl.startsWith('http') || photoUrl.startsWith('data:')) return photoUrl;
    return `${API_URL}${photoUrl}`;
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <PageHeader
        title="Prieteni & Conexiuni"
        subtitle="Persoanele cu care ai interacționat în comunitate"
        icon={<Users className="w-5 h-5" />}
        showBack={true}
        actions={
          <button
            onClick={() => navigate('/community/messages')}
            className="flex items-center gap-2 px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            title="Mesaje private"
          >
            <MessageCircle className="w-5 h-5" />
            <span className="hidden sm:inline">Mesaje</span>
          </button>
        }
      />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Tabs */}
        <div className="bg-white rounded-lg border border-gray-200 mb-4 overflow-hidden">
          <div className="flex border-b border-gray-200 overflow-x-auto">
            {tabs.map((t) => (
              <button
                key={t.key}
                onClick={() => changeTab(t.key)}
                className={`flex-shrink-0 flex items-center justify-center gap-2 px-3 py-3 text-sm font-medium transition-colors relative ${
                  activeTab === t.key
                    ? t.key === 'blocked'
                      ? 'text-red-600 bg-red-50'
                      : 'text-blue-600 bg-blue-50'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                {t.icon}
                <span className="hidden sm:inline">{t.label}</span>
                <span className="sm:hidden text-xs">{t.label}</span>
                {t.count > 0 && (
                  <span
                    className={`ml-1 px-2 py-0.5 rounded-full text-xs font-semibold ${
                      activeTab === t.key
                        ? t.key === 'blocked'
                          ? 'bg-red-600 text-white'
                          : 'bg-blue-600 text-white'
                        : 'bg-gray-200 text-gray-700'
                    }`}
                  >
                    {t.count}
                  </span>
                )}
                {activeTab === t.key && (
                  <div className={`absolute bottom-0 left-0 right-0 h-0.5 ${t.key === 'blocked' ? 'bg-red-600' : 'bg-blue-600'}`} />
                )}
              </button>
            ))}
          </div>

          {/* Search */}
          <div className="p-3 border-b border-gray-100">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Caută după nume..."
                className="w-full pl-10 pr-10 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:bg-white"
              />
              {query && (
                <button
                  onClick={() => setQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* List */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
            <p className="mt-3 text-sm text-gray-500">Se încarcă...</p>
          </div>
        ) : activeTab === 'blocked' ? (
          /* ── BLOCKED TAB ── */
          filteredBlocked.length === 0 ? (
            <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
              <ShieldOff className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <h3 className="font-semibold text-gray-900 mb-1">
                {query ? 'Niciun rezultat' : 'Nu ai blocat pe nimeni'}
              </h3>
              <p className="text-sm text-gray-500">
                {query
                  ? 'Încearcă un alt termen de căutare.'
                  : 'Utilizatorii blocați nu îți pot trimite mesaje și nu îți pot vedea activitatea.'}
              </p>
              {query && (
                <button onClick={() => setQuery('')} className="mt-3 text-sm text-blue-600 hover:underline">
                  Golește căutarea
                </button>
              )}
            </div>
          ) : (
            <div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-100">
              {filteredBlocked.map((user) => {

                const photo = getPhotoUrl(user.profilePhotoUrl);
                const initials = (user.firstName?.[0] || 'U').toUpperCase();
                return (
                  <div
                    key={user.id}
                    className="flex items-center justify-between gap-3 px-4 py-3 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      {/* Avatar */}
                      <div className="relative flex-shrink-0">
                        {photo ? (
                          <img
                            src={photo}
                            alt={`${user.firstName} ${user.lastName}`}
                            className="w-12 h-12 rounded-full object-cover border-2 border-white shadow-sm opacity-60"
                          />
                        ) : (
                          <div className="w-12 h-12 bg-gray-300 rounded-full flex items-center justify-center text-white font-semibold opacity-60">
                            {initials}
                          </div>
                        )}
                        <span className="absolute -bottom-1 -right-1 w-5 h-5 bg-red-500 rounded-full border-2 border-white flex items-center justify-center">
                          <ShieldOff className="w-2.5 h-2.5 text-white" />
                        </span>
                      </div>

                      {/* Name */}
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-700 truncate">
                          {user.firstName} {user.lastName}
                        </p>
                        <p className="text-xs text-red-500 mt-0.5">Blocat</p>
                      </div>
                    </div>

                    {/* Unblock button */}
                    <button
                      onClick={() => handleUnblock(user.id)}
                      className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-100 text-gray-700 border border-gray-300 hover:bg-green-50 hover:text-green-700 hover:border-green-300 transition-colors text-sm font-medium"
                      title="Deblochează utilizatorul"
                    >
                      <ShieldCheck className="w-4 h-4" />
                      <span className="hidden sm:inline">Deblochează</span>
                    </button>
                  </div>
                );
              })}
            </div>
          )
        ) : currentList.length === 0 ? (
          <EmptyState tab={activeTab} hasSearch={!!query} onReset={() => setQuery('')} />
        ) : (
          /* ── FRIENDS / FOLLOWING / FOLLOWERS TABS ── */
          <div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-100">

            {currentList.map((user) => {
              const photo = getPhotoUrl(user.profilePhotoUrl);
              const initials = (user.firstName?.[0] || 'U').toUpperCase();
              const isSpecialist = user.userType === 'SPECIALIST';
              return (
                <div
                  key={user.id}
                  className="flex items-center justify-between gap-3 px-4 py-3 hover:bg-gray-50 transition-colors"
                >
                  <button
                    onClick={() => navigate(`/community/messages/${user.id}`)}
                    className="flex items-center gap-3 flex-1 min-w-0 text-left"
                  >
                    {/* Avatar */}
                    <div className="relative flex-shrink-0">
                      {photo ? (
                        <img
                          src={photo}
                          alt={`${user.firstName} ${user.lastName}`}
                          className="w-12 h-12 rounded-full object-cover border-2 border-white shadow-sm"
                        />
                      ) : (
                        <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold">
                          {initials}
                        </div>
                      )}
                      {isSpecialist && (
                        <span
                          className="absolute -bottom-1 -right-1 w-5 h-5 bg-blue-600 rounded-full border-2 border-white flex items-center justify-center"
                          title="Specialist verificat"
                        >
                          <Stethoscope className="w-2.5 h-2.5 text-white" />
                        </span>
                      )}
                    </div>

                    {/* Name & badges */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-gray-900 truncate">
                          {user.firstName} {user.lastName}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        {user.isFriend && (
                          <span className="inline-flex items-center gap-1 text-xs text-green-700 bg-green-50 px-2 py-0.5 rounded-full">
                            <Users className="w-3 h-3" />
                            Prieten
                          </span>
                        )}
                        {!user.isFriend && user.iFollow && (
                          <span className="text-xs text-gray-500">Îl urmărești</span>
                        )}
                        {!user.isFriend && !user.iFollow && user.followsMe && (
                          <span className="text-xs text-purple-600">Te urmărește</span>
                        )}
                        {isSpecialist && (
                          <span className="text-xs text-blue-600">Specialist</span>
                        )}
                      </div>
                    </div>
                  </button>

                  {/* Actions */}
                  <div className="flex-shrink-0">
                    <UserActionButtons userId={user.id} compact onStatusChange={() => loadAll()} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function EmptyState({
  tab,
  hasSearch,
  onReset,
}: {
  tab: TabKey;
  hasSearch: boolean;
  onReset: () => void;
}) {
  const navigate = useNavigate();

  if (hasSearch) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
        <Search className="w-10 h-10 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-600 mb-3">Niciun rezultat găsit</p>
        <button onClick={onReset} className="text-sm text-blue-600 hover:underline">
          Golește căutarea
        </button>
      </div>
    );
  }

  const content: Record<string, { icon: React.ReactNode; title: string; desc: string; cta: string; action: () => void }> = {
    friends: {
      icon: <Users className="w-10 h-10 text-gray-300 mx-auto mb-3" />,
      title: 'Încă nu ai prieteni',
      desc: 'Devino prieten cu cineva urmărindu-l — când și el te va urmări, veți fi prieteni și veți putea discuta în privat mai ușor.',
      cta: 'Explorează comunitatea',
      action: () => navigate('/community'),
    },
    following: {
      icon: <UserCheck className="w-10 h-10 text-gray-300 mx-auto mb-3" />,
      title: 'Nu urmărești pe nimeni încă',
      desc: 'Urmărește oameni ale căror postări le apreciezi din comunitate.',
      cta: 'Vezi postări recente',
      action: () => navigate('/community'),
    },
    followers: {
      icon: <UserPlus className="w-10 h-10 text-gray-300 mx-auto mb-3" />,
      title: 'Nu te urmărește nimeni încă',
      desc: 'Postează în comunitate pentru a începe să fii remarcat de ceilalți.',
      cta: 'Creează o postare',
      action: () => navigate('/community/create-post'),
    },
    blocked: {
      icon: <ShieldOff className="w-10 h-10 text-gray-300 mx-auto mb-3" />,
      title: 'Nu ai blocat pe nimeni',
      desc: 'Utilizatorii blocați nu îți pot trimite mesaje și nu îți pot vedea activitatea.',
      cta: 'Înapoi la prieteni',
      action: () => navigate('/community/friends'),
    },
  };

  const c = content[tab];

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
      {c.icon}
      <h3 className="font-semibold text-gray-900 mb-1">{c.title}</h3>
      <p className="text-sm text-gray-500 mb-4 max-w-md mx-auto">{c.desc}</p>
      <button
        onClick={c.action}
        className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
      >
        {c.cta}
      </button>
    </div>
  );
}
